import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import "./LandingPage.css"; // Reuse the landing background and shared styles

const RAW_BACKEND_BASE_URL =
  process.env.REACT_APP_API_ENDPOINT ||
  "https://freaky-keisha-backend.onrender.com";
// Normalize backend base and ensure exactly one /api prefix when building URLs
const buildApiUrl = (endpointPath) => {
  try {
    let base = RAW_BACKEND_BASE_URL.trim();
    // Remove trailing slashes
    base = base.replace(/\/+$/, "");
    // Strip any trailing /api or /api/chat from env to avoid double-prefix
    base = base.replace(/\/api(?:\/chat)?$/i, "");
    const path = endpointPath.startsWith("/")
      ? endpointPath
      : `/${endpointPath}`;
    return `${base}/api${path}`;
  } catch (e) {
    // Fallback
    const path = endpointPath.startsWith("/")
      ? endpointPath
      : `/${endpointPath}`;
    return `https://freaky-keisha-backend.onrender.com/api${path}`;
  }
};

const PRICING = {
  monthly: {
    bitcoin: { amount: 7, currency: "USD" }, // Keep for future use
    paypal: { amount: 9.99, currency: "USD" },
  },
  annual: {
    bitcoin: { amount: 60, currency: "USD" }, // Keep for future use
    paypal: { amount: 99.99, currency: "USD" },
  },
};

const PricingPage = () => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const handleFreeStart = () => {
    // Keep the free CTA sending users to the Bias Detection app
    window.location.href = "/bias-detection";
  };

  // Render PayPal Subscription Buttons
  useEffect(() => {
    const renderSubscriptionButtons = () => {
      try {
        if (window.paypal && window.paypal.Buttons) {
          // Monthly subscription button
          const monthlyContainer =
            "#paypal-button-container-P-8K162008B6278645KNCQLXVA";
          if (document.querySelector(monthlyContainer)) {
            window.paypal
              .Buttons({
                style: {
                  shape: "rect",
                  color: "gold",
                  layout: "vertical",
                  label: "subscribe",
                },
                createSubscription: function (data, actions) {
                  return actions.subscription.create({
                    plan_id: "P-8K162008B6278645KNCQLXVA",
                  });
                },
                onApprove: function (data, actions) {
                  // Redirect to thank you page with subscription info
                  window.location.href = `/thank-you?method=paypal&tier=monthly&amount=9.99&subscription_id=${data.subscriptionID}`;
                },
              })
              .render(monthlyContainer);
          }

          // Annual subscription button
          const annualContainer =
            "#paypal-button-container-P-9SS68339R8880870MNCQN4II";
          if (document.querySelector(annualContainer)) {
            window.paypal
              .Buttons({
                style: {
                  shape: "rect",
                  color: "gold",
                  layout: "vertical",
                  label: "subscribe",
                },
                createSubscription: function (data, actions) {
                  return actions.subscription.create({
                    plan_id: "P-9SS68339R8880870MNCQN4II",
                  });
                },
                onApprove: function (data, actions) {
                  // Redirect to thank you page with subscription info
                  window.location.href = `/thank-you?method=paypal&tier=annual&amount=99.99&subscription_id=${data.subscriptionID}`;
                },
              })
              .render(annualContainer);
          }
        }
      } catch (e) {
        console.error("Failed to render PayPal Subscription Buttons:", e);
      }
    };

    if (
      document.readyState === "complete" ||
      document.readyState === "interactive"
    ) {
      renderSubscriptionButtons();
    } else {
      window.addEventListener("DOMContentLoaded", renderSubscriptionButtons, {
        once: true,
      });
      return () =>
        window.removeEventListener(
          "DOMContentLoaded",
          renderSubscriptionButtons,
        );
    }
  }, []);

  const handleBitcoinCheckout = async (tier) => {
    const selected = PRICING[tier]?.bitcoin;
    if (!selected) {
      console.error("Invalid Bitcoin pricing for tier:", tier);
      return;
    }

    const body = {
      amount: selected.amount,
      currency: selected.currency,
      tier,
      method: "bitcoin",
      product: "Keisha News",
    };

    try {
      const resp = await fetch(buildApiUrl("/create-coinbase-charge"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        let msg = `Failed to initiate Coinbase checkout (${resp.status})`;
        try {
          const j = await resp.json();
          msg = j?.details || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const text = await resp.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Invalid response from backend.");
      }

      if (data?.hosted_url) {
        window.location.href = data.hosted_url;
      } else {
        throw new Error("hosted_url missing in backend response");
      }
    } catch (e) {
      alert(`Bitcoin checkout error: ${e.message}`);
      console.error("Bitcoin checkout error:", e);
    }
  };

  const handlePayPalCheckout = async (tier) => {
    const selected = PRICING[tier]?.paypal;
    if (!selected) {
      console.error("Invalid PayPal pricing for tier:", tier);
      return;
    }

    // Assumes a backend endpoint that creates a PayPal order and returns an approval URL
    try {
      const resp = await fetch(buildApiUrl("/paypal/create-order"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: selected.amount,
          currency: selected.currency,
          tier,
          method: "paypal",
          product: "Keisha News",
          return_url: `${window.location.origin}/thank-you?method=paypal&tier=${tier}&amount=${selected.amount}`,
          cancel_url: `${window.location.origin}/pricing`,
        }),
      });

      if (!resp.ok) {
        let msg = `Failed to initiate PayPal checkout (${resp.status})`;
        try {
          const j = await resp.json();
          msg = j?.message || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await resp.json();
      const approvalUrl =
        data?.approvalUrl ||
        data?.approve_url ||
        data?.links?.find((l) => l.rel === "approve")?.href;
      if (approvalUrl) {
        window.location.href = approvalUrl;
      } else {
        throw new Error("Approval URL missing in backend response");
      }
    } catch (e) {
      alert(
        `PayPal checkout error: ${e.message}. If you are still configuring PayPal, ensure the backend /api/paypal/create-order endpoint is set up.`,
      );
      console.error("PayPal checkout error:", e);
    }
  };

  return (
    <div className="landing-container pricing-page">
      <div
        className="pricing-banner"
        style={{
          background: "rgba(0, 255, 255, 0.08)",
          border: "1px solid rgba(0, 255, 255, 0.3)",
          padding: "12px 16px",
          borderRadius: "10px",
          marginBottom: "16px",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <a
          href="/bias-detection"
          style={{ color: "var(--accent-color)", textDecoration: "underline" }}
        >
          Try the Fragile News Decoder now — no sign in required (3 free daily
          analyses)
        </a>
      </div>
      {/* Navigation Header */}
      <nav className="navigation-header">
        <div className="nav-links">
          <Link to="/" className="nav-link">
            Home
          </Link>
          <div
            className="nav-item dropdown"
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <NavLink to="/knn" className="nav-link">
              Keisha News Network (KNN)
            </NavLink>
            {isDropdownOpen && (
              <div className="dropdown-menu">
                <Link to="/bias-detection" className="dropdown-item">
                  Fragile News Decoder
                </Link>
                <a
                  href="https://maga.talk2keisha.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dropdown-item"
                >
                  The MAGA Files
                </a>
                <a
                  href="https://fnsnews.talk2keisha.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dropdown-item"
                >
                  FNS (Fragile News Source) beta
                </a>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Pricing Container */}
      <div
        className="pricing-container"
        style={{ position: "relative", zIndex: 1, marginTop: "20px" }}
      >
        {/* Fragile News Decoder Micro-SaaS Section */}
        <section
          className="keisha-news-pricing"
          style={{
            position: "relative",
            overflow: "hidden",
          }}
        >
          <h2 style={{ marginBottom: "10px", fontSize: "1.8rem" }}>
            Fragile News Decoder
          </h2>
          <p style={{ marginBottom: "20px", fontSize: "1rem" }}>
            News article bias detection service - Available now
          </p>
          <div
            className="pricing-tiers"
            style={{
              position: "relative",
              zIndex: 100,
            }}
          >
            {/* Clean CSS - no more cropping needed with subscription buttons */}
            <style>{`
              /* Ensure all tiers are visible */
              .tier-free, .tier-monthly, .tier-annual {
                display: block !important;
                visibility: visible !important;
              }
            `}</style>
            {/* Free Tier */}
            <div className="tier-free">
              <h3>Free</h3>
              <div className="tier-price">$0</div>
              <div className="tier-usage">3 uses per day</div>
              <ul className="tier-features">
                <li>Basic bias detection</li>
                <li>IP-based tracking</li>
                <li>Daily usage reset</li>
              </ul>
              <button
                className="tier-button free-tier-btn"
                onClick={handleFreeStart}
              >
                Get Started Free
              </button>
            </div>

            {/* Monthly Tier */}
            <div className="tier-monthly">
              <h3>Monthly</h3>
              <div className="tier-pricing-options">
                {/* Bitcoin option hidden - keep code for future use
                <div className="bitcoin-price recommended">
                  <span className="price-label">💰 Bitcoin Payment</span>
                  <div className="tier-price">$7/month</div>
                  <span className="price-incentive">53% OFF - Best Value!</span>
                  <div className="savings-highlight">Save $7.99/month vs PayPal</div>
                </div>
                */}
                <div
                  className="paypal-price"
                  style={{ width: "100%", textAlign: "center" }}
                >
                  <span className="price-label">💳 PayPal Payment</span>
                  <div className="tier-price">$9.99/month</div>
                  <div className="standard-price">Simple, Secure Payment</div>
                </div>
              </div>
              <div className="tier-usage">10 uses per day</div>
              <div className="usage-details">
                <span className="usage-comparison">vs 3 free daily uses</span>
              </div>
              <ul className="tier-features">
                <li>Advanced bias detection</li>
                <li>User account</li>
                <li>Priority support</li>
                <li>
                  Dismantled newsletter: daily decoded news on racism/white
                  supremacy
                </li>
              </ul>
              <div className="payment-buttons">
                {/* Bitcoin button hidden - keep code for future use
                <button
                  className="tier-button bitcoin-btn"
                  onClick={() => alert('Bitcoin payments temporarily unavailable. Please use PayPal.')}
                  style={{ opacity: 0.6 }}
                >
                  Pay with Bitcoin - $7/mo (Temporarily Unavailable)
                </button>
                */}
                {/* PayPal Subscription Button (Monthly) */}
                <div
                  id="paypal-button-container-P-8K162008B6278645KNCQLXVA"
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>

            {/* Annual Tier */}
            <div
              className="tier-annual"
              style={{ position: "relative", zIndex: 10 }}
            >
              <h3>Annual</h3>
              <div className="tier-pricing-options">
                {/* Bitcoin option hidden - keep code for future use
                <div className="bitcoin-price recommended">
                  <span className="price-label">💰 Bitcoin Payment</span>
                  <div className="tier-price">$60/year</div>
                  <span className="price-incentive">40% OFF - Maximum Savings!</span>
                  <div className="savings-highlight">
                    Save $39.99/year vs PayPal
                    Save $24/year vs monthly Bitcoin
                  </div>
                </div>
                */}
                <div
                  className="paypal-price"
                  style={{ width: "100%", textAlign: "center" }}
                >
                  <span className="price-label">💳 PayPal Payment</span>
                  <div className="tier-price">$99.99/year</div>
                  <div className="savings-note">
                    Save $19.89/year vs monthly PayPal
                  </div>
                </div>
              </div>
              <div className="tier-usage">Unlimited uses</div>
              <div className="usage-details">
                <span className="usage-comparison">No daily limits</span>
              </div>
              <ul className="tier-features">
                <li>Unlimited bias detection</li>
                <li>Priority support</li>
                <li>Export capabilities (coming soon)</li>
                <li>API access (coming soon)</li>
              </ul>
              <div className="payment-buttons">
                {/* Bitcoin button hidden - keep code for future use
                <button
                  className="tier-button bitcoin-btn"
                  onClick={() => alert('Bitcoin payments temporarily unavailable. Please use PayPal.')}
                  style={{ opacity: 0.6 }}
                >
                  Pay with Bitcoin - $60/year (Temporarily Unavailable)
                </button>
                */}
                {/* PayPal Subscription Button (Annual) */}
                <div
                  id="paypal-button-container-P-9SS68339R8880870MNCQN4II"
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>
          </div>
          <div
            className="enterprise-offer-note"
            style={{
              marginTop: "20px",
              background: "rgba(0, 255, 255, 0.08)",
              border: "1px dashed rgba(0, 255, 255, 0.4)",
              padding: "12px 16px",
              borderRadius: "10px",
              textAlign: "center",
            }}
          >
            <strong>Enterprise Solutions:</strong> Priority access for
            enterprise clients. Complete our onboarding form to discuss tailored
            solutions for your organization.
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLScISLSjeGB5Kk1ohhSsTnMXoDdQdWKlzUlitf-dGoqo3lCceA/viewform?usp=sharing&ouid=112402650193591447711"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginLeft: 8,
                color: "var(--accent-color)",
                textDecoration: "underline",
              }}
            >
              Begin Enterprise Onboarding
            </a>
          </div>
        </section>

        {/* Keisha AI Main Service Section - Compact */}
        <section
          className="keisha-ai-pricing"
          style={{
            marginTop: "40px",
            textAlign: "center",
            background: "rgba(0, 255, 136, 0.05)",
            border: "1px solid rgba(0, 255, 136, 0.2)",
            borderRadius: "15px",
            padding: "30px 20px",
          }}
        >
          <h2
            style={{
              color: "#00ff88",
              marginBottom: "10px",
              fontSize: "1.8rem",
            }}
          >
            Keisha AI
          </h2>
          <p style={{ marginBottom: "20px", fontSize: "1rem" }}>
            Advanced AI ethics platform for enterprise bias detection and white
            fragility analysis
          </p>
          <div className="waitlist-cta">
            <h3
              style={{
                color: "#00ffff",
                marginBottom: "10px",
                fontSize: "1.3rem",
              }}
            >
              Coming Soon
            </h3>
            <p style={{ marginBottom: "15px", fontSize: "0.9rem" }}>
              Join our waitlist to be the first to access our comprehensive AI
              ethics platform
            </p>
            <a
              href="/#waitlist"
              className="cta-button"
              style={{
                background: "linear-gradient(45deg, #00ff88, #00ffff)",
                color: "#000000",
                padding: "10px 20px",
                borderRadius: "8px",
                textDecoration: "none",
                fontWeight: "bold",
                fontSize: "0.9rem",
              }}
            >
              Join the Waitlist
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PricingPage;
